/**
 * Settings → Integrations (contracts integrations.ts): incoming webhooks (Slack-style secret URLs that post into a
 * conversation) and the agents connected to the workspace. Pure rules shared by every platform.
 */
import type { App, AppScope, IncomingWebhook, Member } from "@bundle/contracts";
import { isRestricted } from "./access.ts";

export const WEBHOOK_NAME_MAX = 80;

/** Newest first (ties by id, newest first). */
export function sortWebhooks(list: readonly IncomingWebhook[]): IncomingWebhook[] {
  return [...list].sort((a, b) => b.createdAt - a.createdAt || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
}

export function upsertWebhook(list: readonly IncomingWebhook[], w: IncomingWebhook): IncomingWebhook[] {
  return sortWebhooks([w, ...list.filter((x) => x.id !== w.id)]);
}

/** Why a webhook name can't be used: empty, or longer than 80 characters. undefined = fine. */
export function webhookNameProblem(name: string): "empty" | "tooLong" | undefined {
  const n = name.trim();
  if (n === "") return "empty";
  if ([...n].length > WEBHOOK_NAME_MAX) return "tooLong";
  return undefined;
}

/** Members add webhooks; guests (restricted) and agents don't. */
export function canAddWebhooks(me: Member): boolean {
  return me.kind === "human" && !me.deactivated && !isRestricted(me);
}

/** Agents connected to the workspace (active ones), by name. */
export function connectedAgents(members: readonly Member[]): Member[] {
  return members
    .filter((m) => m.kind === "agent" && !m.deactivated && m.agent?.appId === undefined)
    .toSorted((a, b) => a.displayName.localeCompare(b.displayName, "en") || (a.userId < b.userId ? -1 : 1));
}

/** A shell command that posts "Hello" through a webhook URL (shown under the URL, like Slack's setup page). */
export function curlExample(url: string): string {
  return `curl -X POST -H 'Content-Type: application/json' -d '{"text":"Hello"}' ${url}`;
}

// ---------- Apps, slash commands, outgoing webhooks (contracts apps.ts) ----------

/** The bot user of an app (listed under Apps, not Agents). */
export function isAppBot(m: Member): boolean {
  return m.kind === "agent" && m.agent?.appId !== undefined;
}

/** Newest installed first (ties by id, newest first). */
export function sortApps(list: readonly App[]): App[] {
  return [...list].sort((a, b) => b.installedAt - a.installedAt || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
}

export function upsertApp(list: readonly App[], app: App): App[] {
  return sortApps([app, ...list.filter((x) => x.id !== app.id)]);
}

/** Owners and admins (not guests) make and manage apps. */
export function canManageApps(me: Member): boolean {
  return me.kind === "human" && !me.deactivated && !isRestricted(me) && (me.role === "owner" || me.role === "admin");
}

/** Built-in command names an app can't take (core logic/autocomplete SLASH_COMMANDS plus Slack's reserved ones). */
export const RESERVED_COMMANDS = ["remind", "status", "invite", "mute", "task", "page", "huddle", "me", "shrug", "msg", "dm", "leave", "join", "topic", "who", "away", "active", "feed", "search", "apps", "shortcuts"];

/**
 * Why a slash command name can't be registered: not "/" + 1–31 of [a-z0-9_-] ("invalid"), a built-in ("reserved"),
 * or already in `taken` (another of this app's commands, or one an installed app has). undefined = fine.
 */
export function appCommandProblem(command: string, taken: readonly string[]): "invalid" | "reserved" | "taken" | undefined {
  if (!/^\/[a-z0-9_-]{1,31}$/.test(command)) return "invalid";
  if (RESERVED_COMMANDS.includes(command.slice(1))) return "reserved";
  if (taken.includes(command)) return "taken";
  return undefined;
}

/** "/deploy  prod now" → { command: "/deploy", text: "prod now" }; not a command → undefined. Names are lowercased. */
export function slashInvocation(text: string): { command: string; text: string } | undefined {
  const m = /^\/([A-Za-z0-9_-]{1,31})(?:\s+([\s\S]*))?$/.exec(text.trim());
  if (m === null) return undefined;
  return { command: `/${m[1]!.toLowerCase()}`, text: (m[2] ?? "").trim() };
}

/**
 * Does an outgoing webhook fire for a new message? Its channels (empty = every public channel) and, when it has
 * trigger words, the message must start with one (case-insensitive, then the end or a non-word character).
 * → the matched trigger word, "" for a match without trigger words, or null.
 */
export function outgoingTrigger(
  hook: { channelIds: readonly string[]; triggerWords: readonly string[] },
  msg: { channelId: string; channelPublic: boolean; text: string },
): string | null {
  if (hook.channelIds.length > 0 ? !hook.channelIds.includes(msg.channelId) : !msg.channelPublic) return null;
  if (hook.triggerWords.length === 0) return hook.channelIds.length > 0 ? "" : null;
  const text = msg.text.trimStart().toLowerCase();
  for (const word of hook.triggerWords) {
    const w = word.trim().toLowerCase();
    if (w === "" || !text.startsWith(w)) continue;
    const next = text.charAt(w.length);
    if (next === "" || !/[\p{L}\p{N}_]/u.test(next)) return word.trim();
  }
  return null;
}

/** An outgoing webhook needs a channel or a trigger word (Slack's rule). */
export function outgoingWebhookProblem(req: { channelIds: readonly string[]; triggerWords: readonly string[] }): "needsChannelOrTrigger" | undefined {
  return req.channelIds.length === 0 && req.triggerWords.every((w) => w.trim() === "") ? "needsChannelOrTrigger" : undefined;
}

const SCOPES: readonly AppScope[] = [
  "chat:write", "channels:read", "channels:history", "users:read", "reactions:write", "commands",
  "groups:read", "groups:history", "im:history", "mpim:history", "app_mentions:read", "reactions:read",
];

/** Comma/space separated scopes from an OAuth request → known scopes (unknown ones dropped, order kept, no repeats). */
export function parseScopes(scope: string): AppScope[] {
  const out: AppScope[] = [];
  for (const s of scope.split(/[\s,]+/)) {
    const known = SCOPES.find((x) => x === s);
    if (known !== undefined && !out.includes(known)) out.push(known);
  }
  return out;
}
